/**
 * Keycloak Initialization Factory
 *
 * Инициализирует keycloak-js при старте приложения.
 * Используется как APP_INITIALIZER при useKeycloakAuth=true.
 *
 * @story 16.1
 */
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

const devLog = environment.production ? () => { /* noop */ } : console.log.bind(console);

export function initializeKeycloak(keycloak: KeycloakService, authService: AuthService): () => Promise<boolean> {
  return async () => {
    devLog('[Keycloak Init] START');
    try {
      // КРИТИЧНО: Используем ОДИН домен auth.ealev.ru для ВСЕХ регионов
      // Это необходимо для работы Keycloak session cookies между доменами
      // GEO-routing делается на уровне DNS/CDN, НЕ через разные субдомены auth
      const authUrl = (window as any).__env?.authUrl || environment.keycloak.url;

      devLog('[Keycloak Init] Using auth URL:', authUrl);

      // E2E test support: pre-seeded tokens via Playwright addInitScript (Story 21.x)
      const e2eTokens = (window as any).__e2e_keycloak_token;
      const tokenInitOptions = e2eTokens ? {
        token: e2eTokens.access_token,
        refreshToken: e2eTokens.refresh_token,
        idToken: e2eTokens.id_token,
      } : {};

      if (e2eTokens) {
        devLog('[Keycloak Init] E2E token injection detected — using pre-seeded tokens');
      }

      const result = await keycloak.init({
      config: {
        url: authUrl,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        checkLoginIframe: false,
        pkceMethod: 'S256',
        ...tokenInitOptions,
      },
        // Fix auth-401-zombie: KeycloakBearerInterceptor больше не
        // регистрируется в app.config.ts — Bearer/refresh/redirect-на-401
        // теперь делает наш authInterceptor + errorInterceptor.
        enableBearerInterceptor: false,
      });

      devLog('[Keycloak Init] Init completed, authenticated:', result);

      // Fix auth-401-zombie r4 (P0, gpt): защёлкиваем "сессия была РЕАЛЬНО
      // аутентифицирована" ЭАГЕРЛИ, сразу после успешного init() —
      // authService.hasEverAuthenticated()'s ленивая защёлка (r3) могла
      // потерять этот факт безвозвратно, если clearToken() (упавший
      // refresh) случался ДО первого вызова этого метода откуда-либо ещё.
      if (result) {
        authService.markAuthenticated();
      }

      return result;
    } catch (error) {
      console.error('[Keycloak Init] ERROR:', error);
      return false;
    }
  };
}
